/**
 * return utility methods
 */

import { cardConfig } from '../../../presentation/components/cards/config';
import { appConstants } from '../../../application/configurations/constants';
import envConfig from '../../../application/configurations/envConfig';
import { DataViewModel } from '../../../application/model/data/mapperConfig';
import { DocumentViewModel } from '../../../application/model/documents/mapperConfig';
import { DocumentsModel } from '../../../application/model/documents/model';
import { DataModel } from '../../../application/model/data/model';
import { peopleProperties } from '../../../application/model/people/mapperConfig';
import { PeopleModel } from '../../../application/model/people/model';
import { bestbetsProperties } from '../../../application/model/bestbets/mapperConfig';
import { BestbetsModel } from '../../../application/model/bestbets/model';
import { NewsModel } from '../../../application/model/news/model';

export class UtilityService {
  /**
   * Function to set the page title
   * @param   {string} titleText  title of the page
   */
  static setPageTitle = (titleText: string) => {
    document.title = titleText;
  };

  /**
   * Generates a GUID string.
   * @returns {String} The generated GUID.
   * @example af8a8416-6e18-a307-bd9c-f2c947bbb3aa
   */
  static generateGuid = () => {
    function gp8(s?: boolean) {
      const crypto = window.crypto || window.Crypto;
      const array = new Uint32Array(1);
      const p = crypto.getRandomValues(array)[0].toString(16);

      return s ? '-' + p.slice(0, 4) + '-' + p.slice(4, 8) : p;
    }

    return gp8() + gp8(true) + gp8(true) + gp8();
  };

  static fetchHtmlAndTitle = (token: any) => {
    return fetch(envConfig.didYouKnowUrl, {
      credentials: 'include',
      headers: {
        Authorization: 'Bearer ' + token
      }
    })
      .then(async function (payload) {
        if (payload.status === 200) {
          let res = await payload.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(res, 'text/html');
          //getting title element
          const titleElement = doc.querySelector('#ModalLongTitle');
          if (titleElement instanceof Element) {
            const title = titleElement.textContent || '';
            //hiding modal header
            const scriptHeader = doc.querySelector('.modal-header');
            if (scriptHeader instanceof HTMLElement) {
              scriptHeader.style.display = 'none';
            }
            const serializer = new XMLSerializer();
            const htmlString = serializer.serializeToString(doc);

            return { title, htmlString };
          }
        }
      })
      .catch((error) => {
        console.error('Error fetching HTML and title:', error);

        return null; // or handle the error as needed
      });
  };

  /**
   * Generates current date and time.
   * @returns {String} The generated date & time.
   * @example 2022-8-9T12:4:49
   */
  static getCurrentDateTime = () => {
    const today = new Date();
    const date =
      today.getFullYear() +
      '-' +
      (today.getMonth() + 1) +
      '-' +
      today.getDate();
    const time =
      today.getHours() + ':' + today.getMinutes() + ':' + today.getSeconds();

    return date + 'T' + time;
  };

  /**
   * Generate the current date and time in UTC ISO String format.
   * @returns {String} Generated date & time.
   * @example '2023-03-14T07:24:46.062Z'
   */
  static getCurrentUTCDateTime = () => {
    const dateAndTime = new Date();

    return dateAndTime.toISOString();
  };

  /**
   * clear local storage and session storage
   */
  static clearSiteData = () => {
    localStorage.clear();
    sessionStorage.clear();
  };

  /**
   * clear the local storage key accessConfig
   */
  static clearAccessInfo = () => {
    localStorage.removeItem('accessConfig');
  };

  /**
   * utility function to dynamically build search result object
   */
  static getMappedProperties = (
    itemProperty: any,
    itemSelectedProps: { [key: string]: string },
    item: { [key: string]: string | undefined | number } = {}
  ) => {
    if (itemProperty) {
      /**
       * accessing the value property(RHS) of mapping configuration
       * to create object with keys(LHS) of the mapping configuration
       */
      Object.keys(itemProperty).map((oldKey) => {
        let newKey = itemSelectedProps[oldKey] ? itemSelectedProps[oldKey] : '';
        oldKey =
          oldKey === 'hitHighlightedProperties'
            ? 'HitHighlightedProperties'
            : oldKey;
        item[newKey] = itemProperty[oldKey];
      });
    }
    // set card type here for different types of UI cards
    // like - Documents/Credentials/Documents|Solution....
    item = this.getCardType(item);

    // set detail path for different content types based on the vertical.
    item = this.getItemDetailPath(item);

    //set hit highlighting for people
    if (item.vertical === appConstants.verticals.People) {
      item = this.getPeopleHitHighlightedText(item);
      // if (item.ESC_ENGAGEMENTHISTORYSERVICEPERFORMED != undefined) {
      //   item.engagementHistoryServicePerformed = item.ESC_ENGAGEMENTHISTORYSERVICEPERFORMED;
      // }
      // if (item.ESC_CERTIFICATION != undefined) {
      //   item.certifications = item.ESC_CERTIFICATION;
      // }
      if (item.ESC_ABOUTME != undefined) {
         item.aboutme = item.ESC_ABOUTME;
       }
       if (item.ESC_PROFILEPATH != undefined) {
          item.detailPath = item.ESC_PROFILEPATH;
       }
       if (item.ESC_AADOBJECTID != undefined) {
          item.accountId = item.ESC_AADOBJECTID;
       }
       if (item.ESC_WORKEMAIL != undefined) {
          item.email = item.ESC_WORKEMAIL;
       }
       
       
    }

    if (item.vertical === appConstants.verticals.Data) {
      if (item.ESC_EYKDASSETTYPE != undefined) {
        item.assetType = item.ESC_EYKDASSETTYPE;
      }
      if (item.ESC_EYKDLICENSESTATUS != undefined) {
        item.licenseStatus = item.ESC_EYKDLICENSESTATUS;
      }
      if (item.escbase_datastoretypename != undefined) {
        item.contentType = item.escbase_datastoretypename;
      }
      if (item.ESC_EYKDDESCRIPTION != undefined) {
        item.eykdDescription = item.ESC_EYKDDESCRIPTION;
      }
      
    }

    return item;
  };

  /**
   * utility function to dynamically build User result object
   */
  static getMappedUserProperties = (
    itemProperty: any,
    itemSelectedProps: { [key: string]: string },
    item: { [key: string]: string | undefined | number } = {}
  ) => {
    if (itemProperty) {
      /**
       * accessing the value property(RHS) of mapping configuration
       * to create object with keys(LHS) of the mapping configuration
       */
      Object.keys(itemProperty).map((oldKey) => {
        let newKey = itemSelectedProps[oldKey] ? itemSelectedProps[oldKey] : '';
        item[newKey] = itemProperty[oldKey];

        return item;
      });
    }

    return item;
  };

  /**
   * Function to get the meta data properties based on the vertical type
   * @param vertical search vertical
   * @return Meta data properties for selected veritcal type
   **/
  static getRequestModel = (vertical: string): any => {
    let selectProperties: any = {};
    switch (vertical) {
      case appConstants.verticals.All:
        selectProperties = {
          ...DataViewModel,
          ...DocumentViewModel,
          ...peopleProperties
        };
        break;
      case appConstants.verticals.Documents:
      case appConstants.verticals.Credentials:
      case appConstants.verticals.Videos:
        selectProperties = DocumentViewModel;
        break;
      case appConstants.verticals.Data:
        selectProperties = DataViewModel;
        break;
      case appConstants.verticals.People:
        selectProperties = peopleProperties;
        break;
      case appConstants.BestBets:
        selectProperties = bestbetsProperties;
        break;
      default:
        selectProperties = DocumentViewModel;
        break;
    }

    return selectProperties;
  };

  /**
   * Function to get the search results response meta data properties based on the vertical type
   * @param vertical search vertical
   * @param item search result row data
   * @return Meta data properties for veritcal type
   **/
  static getResponseModel = (vertical: string, item: any): any => {
    let modelRecord: any = {};
    let data: any = {};
    switch (vertical) {
      case appConstants.verticals.Data:
        modelRecord = new DataModel(item);
        data = modelRecord.data[0];
        break;
      case appConstants.verticals.People:
        modelRecord = new PeopleModel(item);
        data = modelRecord.peopleData[0];
        break;
      case appConstants.BestBets:
        modelRecord = new BestbetsModel(item);
        data = modelRecord.bestbetsData[0];
        break;
      case appConstants.verticals.News:
        modelRecord = new NewsModel(item);
        data = modelRecord.newsData[0];
        break;
      default:
        modelRecord = new DocumentsModel(item);
        data = modelRecord.documentData[0];
        break;
    }

    return data;
  };

  /**
   * Function to set detail path based on the vertical.
   * @param item Search result item.
   * @returns Search result item with updated detail path value.
   */
  static getItemDetailPath = (item: any): any => {
    if (item.ESC_EYAEMID) {
      item.detailPath = item.url;
    } else {
      switch (item.vertical) {
        case appConstants.verticals.Documents:
          item.detailPath =
            envConfig.legacyDiscoverDetailPath + item.documentId;
          break;

        case appConstants.verticals.Videos:
          item.detailPath = envConfig.legacyDiscoverVideoPath + item.documentId;
          break;

        case appConstants.verticals.Credentials:
          item.detailPath = envConfig.legacyDiscoverDetailPath + item.url;
          break;

        case appConstants.verticals.Data:
          // Returning the actual detail page url as the url field for Data Explorer is updated with the DE Details Page URL + DE Sharepoint Item URL
          item.detailPath = item.url;
          break;

        case appConstants.verticals.News:
          item.detailPath = item.url;
          break;

        default:
          break;
      }
    }

    return item;
  };

  /**
   * Function to transform and return card types for UI rendering.
   * @param item Search Result object.
   * @returns
   */
  static getCardType = (item: any): any => {
    switch (item.vertical) {
      case appConstants.verticals.Documents:
        switch (item.contentType.toLowerCase()) {
          case cardConfig.contentTypes.EYKnowledgeSolutionPage.toLowerCase():
            switch (item.solutionType.toLowerCase()) {
              case cardConfig.solutionTypes.Solution.toLowerCase():
              case cardConfig.solutionTypes.NewSolution.toLowerCase():
                item.cardType = cardConfig.cardTypes.Solutions;
                break;
              case cardConfig.solutionTypes.FieldOfPlay.toLowerCase():
                item.cardType = cardConfig.cardTypes.FieldsOfPlay;
                break;
              case '':
                item.cardType = cardConfig.cardTypes.Solutions;
                break;
              default:
                item.cardType = cardConfig.cardTypes.Documents;
                break;
            }
            break;

          case cardConfig.contentTypes.EYKnowledgeFoPPage.toLowerCase():
            {
              switch (item.solutionType.toLowerCase()) {
                case cardConfig.solutionTypes.BBFOP.toLowerCase():
                  item.cardType = cardConfig.cardTypes.BBFoP;
                  break;

                default:
                  item.cardType = cardConfig.cardTypes.BBFoP;
                  break;
              }
            }
            break;

          case cardConfig.contentTypes.EYMethodDocument.toLowerCase():
            item.cardType = cardConfig.cardTypes.Method;
            break;

          case cardConfig.contentTypes.EYKnowledgePage.toLowerCase():
          case cardConfig.contentTypes.EYTopicPage.toLowerCase():
            item.cardType = cardConfig.cardTypes.KnowledgePage;
            break;

          case cardConfig.contentTypes.EYKnowledgePolicyDocument.toLowerCase():
          case cardConfig.contentTypes.EYPolicyDocument.toLowerCase():
            item.cardType = cardConfig.cardTypes.Policies;
            break;

          case cardConfig.contentTypes.EYTasPolicyPage.toLowerCase():
          case cardConfig.contentTypes.EYKnowledgePolicyPage.toLowerCase():
          case cardConfig.contentTypes.EYPolicyPage.toLowerCase():
          case cardConfig.contentTypes.EYPolicyGuidancePage.toLowerCase():
            item.cardType = cardConfig.cardTypes.PagePolicies;
            break;

          case cardConfig.contentTypes.EYKnowledgeBuyerPage.toLowerCase():
            {
              switch (item.solutionType.toLowerCase()) {
                case cardConfig.solutionTypes.Buyer.toLowerCase():
                  item.cardType = cardConfig.cardTypes.BuyerPage;
                  break;

                default:
                  item.cardType = cardConfig.cardTypes.BuyerPage;
                  break;
              }
            }
            break;

          case cardConfig.contentTypes.EYResourceCatalogueItem.toLowerCase():
            item.cardType = cardConfig.cardTypes.Site;
            break;

          case cardConfig.contentTypes.EYDataVisualization.toLowerCase():
            item.cardType = cardConfig.cardTypes.DocumentVis;
            break;

          // Added for EY-P
          case cardConfig.contentTypes.EYPEngagementItem.toLowerCase(): {
            item.cardType = cardConfig.cardTypes.EYPEngagement;
            break;
          }

          case cardConfig.contentTypes.EYPPerspectiveDocument.toLowerCase(): {
            item.cardType = cardConfig.cardTypes.EYPPerspective;
            break;
          }

          default:
            item.cardType = cardConfig.cardTypes.Documents;
            break;
        }
        break;
      case appConstants.verticals.Credentials:
        item.cardType = cardConfig.cardTypes.Credentials;
        break;
      case appConstants.verticals.Data:
        item.cardType = cardConfig.cardTypes.Data;
        break;
      case appConstants.verticals.People:
        item.cardType = cardConfig.cardTypes.People;
        break;
      case appConstants.verticals.Videos:
        switch (item.contentType.toLowerCase()) {
          case cardConfig.contentTypes.EYVideo.toLowerCase():
            item.cardType = cardConfig.cardTypes.Videos;
            break;
        }
        item.cardType = cardConfig.cardTypes.Videos;
        break;
      case appConstants.verticals.News:
        switch (item.contentType.toLowerCase().trim()) {
          case cardConfig.contentTypes.NewsArticle.toLowerCase():
            item.cardType = cardConfig.cardTypes.NewsArticle;
            break;
          case cardConfig.contentTypes.NewsPage.toLowerCase():
            item.cardType = cardConfig.cardTypes.PageIntranet;
            break;
          case cardConfig.contentTypes.NewsVideo.toLowerCase():
            item.cardType = cardConfig.cardTypes.NewsVideo;
            break;
          default:
            item.cardType = cardConfig.cardTypes.NewsArticle;
            break;
        }
        break;
      default:
        if (
          item.contentType.toLowerCase() ===
          cardConfig.contentTypes.NewsPage.toLowerCase()
        ) {
          item.cardType = cardConfig.cardTypes.PageIntranet;
          break;
        } else {
          item.cardType = cardConfig.cardTypes.Documents;
          break;
        }
    }

    return item;
  };

  /**
   * Format the date to be displayed in the card.
   */
  static getFormattedDateForResultCard = (dateString: string) => {
    const month = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec'
    ];

    const tempDate = new Date(dateString);

    return (
      tempDate.getDate() +
      ' ' +
      month[tempDate.getMonth()] +
      ' ' +
      tempDate.getFullYear()
    );
  };

  /*
   * Method gets the people title and excerpt updated with the highlighted text.
   */
  static getPeopleHitHighlightedText = (item: any): any => {
    if (item.HitHighlightedProperties) {
      item.HitHighlightedProperties = JSON.parse(item.HitHighlightedProperties);
    }

    if (
      item.HitHighlightedProperties &&
      item.HitHighlightedProperties.length > 0
    ) {
      const location = item.HitHighlightedProperties.filter(
        (field: any) => field.Key === 'location'
      )[0];
      if (location) {
        item.location = location.Value[0];
      }

      const role = item.HitHighlightedProperties.filter(
        (field: any) => field.Key === 'role'
      )[0];
      if (role) {
        item.role = role.Value[0];
      }
    }

    return item;
  };

  static getPeopleHitHighlightedFieldValue = (
    field: string,
    hitHighlightedProperties: string
  ): string => {
    let fieldValue: string = '';

    const parser = new DOMParser();
    const highlightToParse = parser.parseFromString(
      hitHighlightedProperties,
      'text/xml'
    );

    if (
      highlightToParse.getElementsByTagName(field) &&
      highlightToParse.getElementsByTagName(field).length
    ) {
      let highLightValue =
        highlightToParse.getElementsByTagName(field)[0].textContent;
      //if the response tag contains the <c0> then field will be added to excerpt field for people.
      if (highLightValue && highLightValue.includes('!czeroopen')) {
        //replacing the placeholder with highlighting tags after xml parsing.
        highLightValue = highLightValue.replaceAll('!czeroopen', '<c0>');
        highLightValue = highLightValue.replaceAll('!czeroclose', '</c0>');
        fieldValue = highLightValue;
      }
    }

    return fieldValue;
  };

  //function to implement 2 words before logic for People Vertical
  static twoWordPeopleLogic(fieldValue: any) {
    let replacedText = fieldValue;

    if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
      const regex = /(\S*\s*){2}(<c0>[\S\s]*<\/c0>)[\s\S]*/;
      const matches = fieldValue.toString().match(regex);

      if (matches) {
        replacedText = matches[0];
      }
    }

    return replacedText;
  }

  //function to replace <c0></c0> tags with <span></span> tags
  static applyHighlighting(fieldValue: any) {
    let replacedText = fieldValue;
    if (fieldValue) {
      replacedText = replacedText
        .toString()
        .replaceAll('<c0>', '<span><span class="highlight-text">')
        .replaceAll('</c0>', '</span></span>');
    }

    return replacedText;
  }

  // Returns text without any tags
  static removeHtmlTags(fieldValue: any) {
    try {
      let pureText = fieldValue;
      const regex = /(<([^>]+)?>)/gi;

      if (fieldValue) {
        pureText = pureText.toString().replace(regex, '');
      }

      return pureText;
    } catch (error) {
      console.error('Error removing HTML tags:', error);
      // Return original value if error occurs

      return fieldValue;
    }
  }

  // Returns text without any backslash
  static removeBackslash = (fieldValue: any) =>
    fieldValue ? fieldValue.toString().replace(/(\\+$)/gi, '') : fieldValue;

  /**
   * Function to combine current date and past date from provided month
   * and return as string of dates. this is used by date filter api payload
   * @param priorMonths month for calculate date.
   * @return the dates for months before passed
   */
  static getMonthDates(priorMonths: number) {
    let dates = '';
    if (priorMonths) {
      const toDate = new Date();
      toDate.setMonth(toDate.getMonth() - priorMonths);
      dates = toDate.toISOString();
    }

    return dates;
  }

  /**
   * Function to extract query parameters from URL
   * and return as object
   * @param appUrl site url and @param key to be extracted.
   * @return the object of passed key
   */
  static getUrlParams = (appUrl: any, key: any) => {
    try {
      let resultData = '';
      if (appUrl) {
        const url: any = new URLSearchParams(appUrl);
        const urlSearchParams: any = url && url.get(key);
        if (urlSearchParams && key === 'r') {
          resultData = JSON.parse(urlSearchParams);
        } else if (urlSearchParams) {
          resultData = urlSearchParams;
        }
      }

      return resultData;
    } catch (error) {
      console.log(error);

      return '';
    }
  };

  /**
   * Function to set the page title
   * @param   {string} searchVal  search term
   * @param   {string} verticalText vertical selected
   * @param   {string} updatedSortValue Sort filter value
   * @param   {number} noOfItemsPerPage no of items per page
   * @param   {number} dateFilterValue date filter value
   * @param   {number} currentPageValue current page number
   * @param   {any} filterChipsListValue filter chip list value
   * @param   {function} NavigateFunction Use navigate hook function
   * @param   {any} selectedRefiners selected refiner value

   }}
   */
  static urlUpdate = (
    searchVal: string,
    verticalText: string,
    updatedSortValue: string,
    noOfItemsPerPage: number,
    dateFilterValue: number,
    currentPageValue: number,
    filterChipsListValue: any,
    historyFunc: any,
    selectedRefiners?: any,
    browseVariant?: number,
    searchType?: string
  ) => {
    if (
      verticalText &&
      updatedSortValue &&
      noOfItemsPerPage &&
      filterChipsListValue
    ) {
      historyFunc({
        search: `?k=${encodeURIComponent(searchVal.trim())}${
          browseVariant ? `&b=${encodeURIComponent(browseVariant)}` : ``
        }${
          verticalText.length > 0 && verticalText !== appConstants.verticals.All
            ? `&v=${encodeURIComponent(verticalText)}`
            : ``
        }${`&s=${encodeURIComponent(
          updatedSortValue.toString().trim()
        )}`}${`&pn=${encodeURIComponent(
          currentPageValue
        )}`}${`&n=${encodeURIComponent(
          noOfItemsPerPage
        )}`}${`&r=${encodeURIComponent(
          '[{"contentAge":' + dateFilterValue + '}]'
        )}`}${searchType ? `&c=${encodeURIComponent(searchType)}` : ``}${
          selectedRefiners &&
          selectedRefiners.length > 0 &&
          selectedRefiners !== '[]'
            ? `&f=${encodeURIComponent(
                '[{"refiner":' + selectedRefiners + '}]'
              )}`
            : ``
        }
        ${
          filterChipsListValue.length > 0
            ? `&tag=${encodeURIComponent(
                '[{"selectedFilters":' +
                  JSON.stringify(filterChipsListValue) +
                  '}]'
              )}`
            : ``
        }`
      });
    }
  };

  /**
   * Function to Get the script element and its source file reference
   * @param   {any} resHtml  Header/Footer html element as a text
   }}
   */
  static async scriptLoad(resHtml: any) {
    //Create temp html element
    let htmlElement = document.createElement('html');
    htmlElement.innerHTML = resHtml;

    //Get script tags from the temp html elements
    let childElements = htmlElement.getElementsByTagName('script');

    //Iterate through all the javascript script element
    for (
      let elementIndex = 0;
      elementIndex < childElements.length;
      elementIndex++
    ) {
      //Get the source file reference of the script element.
      let srcScript = childElements[elementIndex].getAttribute('src');
      // console.log(srcScript)
      //Condition to stop load main.js from AEM header
      if (
        srcScript &&
        srcScript.indexOf(envConfig.webcomponent.jsfile) === -1
      ) {
        try {
          //Load the script separately.
          await this.loadSingleScript(srcScript);
        } catch (ex) {
          console.log('Error:' + ex);
        }
      }
    }
  }

  /**
   * Function to Get the CSS style element and its source file reference
   * @param   {any} resHtml  Header/Footer html element as a text
   }}
   */
  static async cssLoad(resHtml: any) {
    //Create temp html element
    let htmlElement = document.createElement('html');
    htmlElement.innerHTML = resHtml;

    //Get style tags from the temp html elements
    let childElements = htmlElement.getElementsByTagName('link');

    //Iterate through all the CSS style element
    for (
      let elementIndex = 0;
      elementIndex < childElements.length;
      elementIndex++
    ) {
      //Get the source file reference of the CSS style element.
      let srcStyle = childElements[elementIndex].getAttribute('href');
      //condition to stop loading main.css from AEM header
      if (srcStyle && srcStyle.indexOf(envConfig.webcomponent.cssfile) === -1) {
        try {
          //Load the style separately.
          await this.loadSingleStyle(srcStyle);
        } catch (ex) {
          console.log('Error:' + ex);
        }
      }
    }
  }

  /**
   * Function to load single script file
   * @param   {string} srcScript  source file of the script tag
   }}
   */
  static loadSingleScript(srcScript: string) {
    return new Promise(function (resolve, reject) {
      let javascriptScript = document.createElement('script');
      javascriptScript.type = 'text/javascript';
      javascriptScript.addEventListener('load', resolve);
      javascriptScript.addEventListener('error', reject);
      javascriptScript.src = srcScript;
      document.head.appendChild(javascriptScript);
    });
  }

  /**
   * Function to load single CSS style file
   * @param   {string} srcStyle  source file of the style tag
   }}
   */
  static loadSingleStyle(srcStyle: string) {
    return new Promise(function (resolve, reject) {
      let styleSheet = document.createElement('link');
      styleSheet.rel = 'stylesheet';
      styleSheet.addEventListener('load', resolve);
      styleSheet.addEventListener('error', reject);
      styleSheet.href = srcStyle;
      document.head.appendChild(styleSheet);
    });
  }

  /**
  * Function to convert string to base64 string
  * @param   {string} txtInput  text/string to convert
  }}
  */
  static Base64 = {
    btoa: (txtInput: string = '') => {
      let strInput = txtInput;
      let output = '';
      const chars =
        'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
      for (
        let block = 0, charCode, i = 0, map = chars;
        strInput.charAt(i | 0) || ((map = '='), i % 1);
        output += map.charAt(63 & (block >> (8 - (i % 1) * 8)))
      ) {
        charCode = strInput.charCodeAt((i += 3 / 4));
        //If base64 conversion fails
        if (charCode > 0xff) {
          throw new Error(
            "'btoa' failed: The string to be encoded contains characters outside of the Latin1 range."
          );
        }

        block = (block << 8) | charCode;
      }

      return output;
    }
  };

  /**
   * Function to convert N-level children and put it in nested fashion.
   */

  static convertRefinersLevel2(data: any) {
    const source: any = [];
    // Logic to sort the data based on the length of the RefinementValue
    data.RefinementResults.sort(
      (a: any, b: any) => a.RefinementValue.length - b.RefinementValue.length
    );
    // function to find or create a source
    function findOrCreateSource(
      arr: any,
      title: any,
      key: any,
      structureKey: any,
      refinerID: any,
      refinerName: any
    ) {
      const existingItem = arr.find(
        (item: any) =>
          item.title === title && item.structureKey === structureKey
      );
      // if found
      if (existingItem) {
        return existingItem;
      } else {
        const newItem = {
          title,
          key,
          structureKey,
          children: [],
          refinerID,
          refinerName
        };
        arr.push(newItem);

        return newItem;
      }
    }

    data.RefinementResults.forEach((item: any) => {
      if (
        item.RefinerName.indexOf('UFPATH') > -1 &&
        item.RefinementName.indexOf('\\') > -1
      ) {
        // Condition to replace the backslash with forward slash
        item.RefinementName = item.RefinementName.replace(/\\/g, '/')
          .split('/')
          .slice(1)
          .join('/')
          .toString();
      }
      /*Logic to remove empty RefinementName*/
      if (item.RefinementName === '') {
        return;
      }
      const levels =
        item.RefinerName.indexOf('UFPATH') > -1
          ? item.RefinementName.split('/')
          : [item.RefinementName];
      const valueLevels =
        item.RefinementValue.indexOf('UFPATH') > -1
          ? item.RefinementValue.split('/')
          : [item.RefinementValue];
      const refinerID = item.RefinementValue;
      const refinerName = item.RefinerName;
      let parentSource: any = null;

      levels.forEach((level: any, index: any) => {
        const structureKey = `${level}_${index}`;
        const key = level + '_' + this.generateGuid();
        const title = level;

        parentSource = findOrCreateSource(
          index === 0 ? source : parentSource.children,
          title,
          key,
          structureKey,
          refinerID,
          refinerName
        );

        // if last level of the refiner structure
        if (index === levels.length - 1) {
          parentSource.children = valueLevels
            .slice(index + 1)
            .map((valueLevel: any, valueIndex: any) => {
              return {
                title: valueLevel,
                key: `${structureKey}_${valueIndex}`,
                children: [],
                refinerID: refinerID
              };
            });
        }
      });
    });

    return source;
  }

  /*
   * Funciton to Convert first level Nested structure.
   */

  static convertRefinersLevel1(data: any) {
    let nestedJson: any = [];
    data.forEach((refinement: any) => {
      const refinerName = refinement.RefinerName;

      // if nestedJson array is not empty
      if (nestedJson.length > 0) {
        nestedJson.map((value: any) => {
          let filtered = nestedJson.filter(
            (title: { title: string }) => title.title === refinerName
          );
          // if nestedJson already has the refinerName
          if (filtered.length > 0) {
            if (value.title === refinerName) {
              value.source.push(refinement);
            }
          } else {
            let object = {
              title: refinerName,
              refId: refinerName + 'Key',
              source: [refinement]
            };
            nestedJson.push(object);
          }
        });
      } else {
        let object1 = {
          title: refinerName,
          refId: refinerName + 'Key',
          source: [refinement]
        };
        nestedJson.push(object1);
      }
    });

    /* logic to filter the nestedJson array which contains non empty RefinementName in source array*/
    nestedJson = nestedJson.filter((item: any) => {
      let filtered = item.source.filter(
        (title: { RefinementName: string }) => title.RefinementName !== ''
      );
      if (filtered.length > 0) {
        return item;
      }
    });

    return nestedJson;
  }

  /*
   * Funciton to add search term with browse config based on vertical in browse page.
   */
  static browseConfiguredSearchTerm(
    searchTerm: string,
    vertical: string,
    BrowseConfig: any
  ) {
    let config: any = '';
    let searchQuery = '';

    config = BrowseConfig.Verticals.filter(
      (configValue: any) =>
        configValue.Name.toLowerCase() === vertical.toLowerCase()
    );

    if (config.length > 0) {
      searchQuery = searchTerm + ' ' + config[0].QueryCondition;
    } else {
      searchQuery = searchTerm;
    }

    return searchQuery;
  }

  static selectedRefiners = (refiners: any, filterObj: any) => {
    if (
      refiners[Object.keys(refiners)[0]] !== undefined &&
      refiners[Object.keys(refiners)[0]].length > 0
    ) {
      // Condition to check if selected refiner is more than one value
      if (refiners[Object.keys(refiners)[0]].length > 1) {
        filterObj.push({
          Field: Object.keys(refiners)[0],
          Type: 'String',
          Operator: ':',
          Values: refiners[Object.keys(refiners)[0]]
        });
      } else {
        filterObj.push({
          Field: Object.keys(refiners)[0],
          Type: 'String',
          Operator: ':',
          Value: refiners[Object.keys(refiners)[0]][0]
        });
      }
    }
  };
}

export const checkIntranetContext = () => {
  return new URLSearchParams(window.location.href).get('c')?.toLowerCase();
};

type ConfigType = 'header' | 'footer';

export const getHeaderFooterConfig = (type: ConfigType): string => {
  // Extract the 'c' query parameter from the current window location
  const queryParamContext = checkIntranetContext();

  // Retrieve the current context from local storage
  const context = localStorage.getItem('context');

  // Check if the 'c' query parameter is defined or context is intranet
  const isIntranet =
    (queryParamContext !== undefined && queryParamContext !== null) ||
    context === appConstants.intranet;

  if (isIntranet) {
    // Only remove context from local storage if type is 'footer'
    if (type === 'footer') {
      localStorage.removeItem('context');
    }

    return type === 'header'
      ? envConfig.intranetHeader
      : envConfig.intranetFooter;
  }

  // Default to the discover configuration for other cases
  return type === 'header'
    ? envConfig.discoverHeader
    : envConfig.discoverFooter;
};
